import { useSchedule } from "../hooks/useSchedule";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { formatCurrency } from "@poseidon/utils/currency";
import React, { useMemo } from "react";
import { useMakePayment } from "../hooks/useMakePayment";
import { usePermissions } from "@poseidon/ui/components";

import { styled } from "@mui/material/styles";
import { useDocument } from "react-firebase-hooks/firestore";
import { Panel } from "@poseidon/ui/components/Panel";
import { getDate } from "@poseidon/firebase/timestamp";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&.status--paid": {
    backgroundColor: theme.palette.success.dark,
    color: theme.palette.success.contrastText,
  },
  "&.status--due": {
    backgroundColor: theme.palette.error.dark,
    color: theme.palette.error.contrastText,
  },
}));

const PaymentRow = ({
  data,
  handleMarkPaid,
  saving,
  loading,
  allowPay,
  finalBalloon,
}) => {
  if (loading || !data) {
    return (
      <StyledTableRow>
        <TableCell colSpan="8">
          <Skeleton variant="text" />
        </TableCell>
      </StyledTableRow>
    );
  }
  return (
    <StyledTableRow>
      <TableCell>
        #{data.number}
        {finalBalloon && (
          <Chip
            label="Final payment incl. balloon"
            color="info"
            size="small"
            sx={{ display: "flex", mt: 0.5, width: "fit-content" }}
          />
        )}
      </TableCell>
      <TableCell>{data.date.toLocaleDateString("en-GB")}</TableCell>
      <StyledTableCell className={`status--${data.status}`}>
        {data.status}
      </StyledTableCell>
      <TableCell>{formatCurrency(data.payment, true)}</TableCell>
      <TableCell>{formatCurrency(data.interest)}</TableCell>
      <TableCell>{formatCurrency(data.capitalRepayment)}</TableCell>
      <TableCell>{formatCurrency(data.balance)}</TableCell>
      <TableCell>
        {(data.status === "pending" || data.status === "due") && allowPay && (
          <Button
            variant="outlined"
            size="small"
            disabled={saving}
            onClick={() => handleMarkPaid(data.id, data.scheduleId)}
          >
            Mark Paid
          </Button>
        )}
      </TableCell>
    </StyledTableRow>
  );
};

const PastPaymentRow = ({ docRef, canWrite, ...props }) => {
  const [doc, loading] = useDocument(docRef);
  const data = useMemo(() => {
    if (!doc) {
      return undefined;
    }
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      scheduleId: doc.ref.parent.parent.id,
      date: getDate(data.date),
    };
  }, [doc]);

  return (
    <PaymentRow data={data} loading={loading} {...props} allowPay={canWrite} />
  );
};

const ScheduleGroupRow = ({ scheduleId, schedules }) => {
  const index = schedules.findIndex((s) => s.id === scheduleId);
  const schedule = schedules[index];
  return (
    <StyledTableRow>
      <TableCell colSpan={8}>
        Schedule #{index + 1} - {schedule?.status}
      </TableCell>
    </StyledTableRow>
  );
};

export const PaymentsTable = ({ agreement, scheduleId, schedules }) => {
  const { canWrite } = usePermissions();
  const { schedule, loading, error } = useSchedule(scheduleId);
  const makePayment = useMakePayment();
  const handleMarkPaid = (paymentId, scheduleId) => {
    makePayment.makePayment(agreement.id, scheduleId, paymentId);
  };
  const allowMarkPaidFor = schedule?.payments.find(
    (payment) => payment.status === "due" || payment.status === "pending"
  );
  // On a balloon schedule the last instalment carries the balloon and closes
  // the balance at 0; flag that row so it renders clearly.
  const balloonScheduleFinalId =
    schedule?.parameters?.balloon > 0 && schedule.payments.length
      ? schedule.payments[schedule.payments.length - 1].id
      : null;
  return (
    <Panel nopadding expandable title="Payments">
      {loading && <LinearProgress />}
      {error && "Error!"}
      {!loading && !error && schedule && (
        <Table>
          <TableHead>
            <StyledTableRow>
              <TableCell component="th">Number</TableCell>
              <TableCell component="th">Date</TableCell>
              <TableCell component="th">Status</TableCell>
              <TableCell component="th">Payment</TableCell>
              <TableCell component="th">Interest</TableCell>
              <TableCell component="th">Capital Repayment</TableCell>
              <TableCell component="th">Balance</TableCell>
              <TableCell component="th"></TableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {schedule.pastPayments?.reduce((agg, paymentRef, index, arr) => {
              const prevRow = arr[index - 1];
              const scheduleId = paymentRef.parent.parent.id;
              const prevScheduleId = prevRow?.parent.parent.id;

              const row = (
                <PastPaymentRow
                  key={paymentRef.path}
                  handleMarkPaid={handleMarkPaid}
                  docRef={paymentRef}
                  saving={makePayment.saving}
                  canWrite={canWrite}
                />
              );

              return [
                ...agg,
                ...(prevScheduleId === scheduleId
                  ? []
                  : [
                      <ScheduleGroupRow
                        key={scheduleId}
                        scheduleId={scheduleId}
                        schedules={schedules}
                      />,
                    ]),
                row,
              ];
            }, [])}
            {!!schedule.pastPayments?.length && (
              <ScheduleGroupRow
                key={scheduleId}
                scheduleId={scheduleId}
                schedules={schedules}
              />
            )}
            {schedule.payments.map((payment) => (
              <PaymentRow
                key={payment.id}
                data={payment}
                handleMarkPaid={handleMarkPaid}
                allowPay={allowMarkPaidFor?.id === payment.id && canWrite}
                saving={makePayment.saving}
                finalBalloon={payment.id === balloonScheduleFinalId}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </Panel>
  );
};
